import {
  Box,
  Button,
  Checkbox,
  Dropdown,
  Input,
  Popup,
  Table,
  TableRow,
  TextArea,
  TopBar,
} from '@sam/components';
import { TableHeader } from '@sam/components/src/Table/Table.types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DocumentBucket,
  InventoryCategory,
  InventoryCheck,
  InventoryEntry,
  InventoryGroup,
  InventoryState,
  Language,
  SimpleUser,
  ToolingGroup,
  ToolingType,
  createNewInventoryEntry,
  deleteInventoryEntry,
  generateDropdownOptionsForToolingGroups,
  generateDropdownOptionsForToolingTypes,
  generateNewInventoryEntry,
  generateNotification,
  updateInventoryEntry,
  useData,
} from 'shared';
import { NotificationType } from 'shared/src/notification/notification.types';
import { FileOverview } from '../../components/files/FileOverview';
import { InventoryDetailInformation } from '../../components/inventory/InventoryDetailInformation';
import { SaveButtons } from '../../components/saveButtons/SaveButtons';
import { useUser } from '../../components/UserContext';
import {
  convertEntryChecksIntoTableEntries,
  generateEmptyInventoryCheck,
} from '../../utils/inventory/Inventory.utils';
import { generateDropdownOptionsForSimpleUser, getUserName } from '../../utils/user/User.utils';
import { IconComponent, IconType } from '../../components/icons/IconComponent';
import { preventSubmitOnEnter } from '../../utils/general.utils';

const InventoryCreateOrEdit: React.FC = () => {
  const { t } = useTranslation();
  const { user, axios, updateFootnoteConfig } = useUser();
  const [showDeletePopup, toggleDeletePopup] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation<{
    item?: InventoryEntry;
    group?: InventoryGroup;
    selectedCategories?: string[];
  }>();
  const [inventoryEntry, setInventoryEntry] = useState<InventoryEntry>(
    location.state?.item || generateNewInventoryEntry(location.state?.group || InventoryGroup.ITEM),
  );
  const [checkToEdit, setCheckToEdit] = useState<InventoryCheck>();
  const [checkToDelete, setCheckToDelete] = useState<string>();

  const { data: allOffices } = useData('OFFICES_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: allSimpleUser } = useData('SIMPLEUSERS_ALL_ALL_OFFICES', {
    config: { fallbackData: [] },
  });
  const { data: allCategories } = useData('INVENTORY_CATEGORY_GROUP', {
    config: { fallbackData: [] },
    params: { group: inventoryEntry.group },
  });
  const category: InventoryCategory | undefined = useMemo(
    (): InventoryCategory | undefined =>
      allCategories.find((cat) => cat.id === inventoryEntry.categoryId),
    [allCategories, inventoryEntry.categoryId],
  );
  const button = useRef<HTMLButtonElement>(null);
  const form = useRef<HTMLFormElement>(null);

  /**
   * Hook to update the footNote containg the create and update data
   */
  useEffect(() => {
    updateFootnoteConfig(inventoryEntry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryEntry.createDate]);

  /**
   * Helper to update the inventory item on the given value and key
   *
   * @param newValue The user entered value
   * @param key The key to update
   */
  const updateInfo = (
    newValue: string | number | boolean | Date | undefined,
    key: keyof InventoryEntry,
  ): void => {
    setInventoryEntry((old) => ({ ...old, [key]: newValue }));
  };

  /**
   * Callback to handle the form submit
   * @param redirect decides if the method should navigate back after success
   */
  const handleSubmit = (redirect: boolean): void => {
    button.current?.click();
    if (!form.current?.checkValidity()) return;
    const promise: Promise<InventoryEntry | undefined> = inventoryEntry.id
      ? updateInventoryEntry(axios, inventoryEntry)
      : createNewInventoryEntry(axios, inventoryEntry);
    promise.then((updatedEntry) => {
      if (updatedEntry && redirect)
        navigate(`/inventory/${inventoryEntry.group.toLocaleLowerCase()}`, {
          state: { selectedCategories: location.state?.selectedCategories },
        });
      else if (updatedEntry) {
        setInventoryEntry(updatedEntry);
        generateNotification({
          type: NotificationType.SUCCESS,
          value: t('general.notification.success.saveSuccessfull'),
        });
      }
    });
  };

  /**
   * Holds the table entries for the user history map. Sorts the
   * entries by date descending
   */
  const userHistoryTableEntries = useMemo((): TableRow[] => {
    if (!allSimpleUser) return [];
    return inventoryEntry.userChanges
      .map((change) => {
        let displayText: string = '';
        //userId is set to UNASSIGNED, if an assigned user is removed to display the date of the change
        if (change.newUserId === 'UNASSIGNED')
          displayText = t('pages.inventory.createEdit.notAssigned');
        else {
          const user: SimpleUser | undefined = allSimpleUser.find(
            (user) => user.id === change.newUserId,
          );
          displayText = user ? getUserName(user) : t('pages.inventory.createEdit.userNotFound');
        }
        return {
          id: change.changeDate.toISOString(),
          content: [change.changeDate.toLocaleDateString(), displayText],
        };
      })
      .sort((entry1, entry2) => entry2.id.localeCompare(entry1.id));
  }, [allSimpleUser, inventoryEntry.userChanges, t]);

  /**
   * Holds the rows for checks on the inventoryEntry
   */
  const inventoryCheckRows: TableRow[] = useMemo((): TableRow[] => {
    return convertEntryChecksIntoTableEntries(
      inventoryEntry.checks,
      allSimpleUser,
      setCheckToEdit,
      setCheckToDelete,
    );
  }, [allSimpleUser, inventoryEntry.checks]);

  /**
   * Holds the iconType to display. Only used for contracts
   */
  const stateIcon: IconType | undefined = useMemo((): IconType | undefined => {
    if (inventoryEntry.group !== InventoryGroup.CONTRACT) return undefined;
    switch (inventoryEntry.state) {
      case InventoryState.RED:
        return IconType.TRAFFIC_LIGHT_RED;
      case InventoryState.YELLOW:
        return IconType.TRAFFIC_LIGHT_YELLOW;
      case InventoryState.GREEN:
        return IconType.TRAFFIC_LIGHT_GREEN;
      default:
        return IconType.TRAFFIC_LIGHT_RED;
    }
  }, [inventoryEntry]);

  /**
   * Handler to update the inventoryState
   */
  const handleInventoryStateUpdate = (): void => {
    let newState: InventoryState | undefined;
    switch (inventoryEntry.state) {
      case InventoryState.RED:
      case undefined:
        newState = InventoryState.YELLOW;
        break;
      case InventoryState.YELLOW:
        newState = InventoryState.GREEN;
        break;
      case InventoryState.GREEN:
        newState = InventoryState.RED;
        break;
    }
    setInventoryEntry({ ...inventoryEntry, state: newState });
  };

  const variableFields: JSX.Element = useMemo((): JSX.Element => {
    if (inventoryEntry.group === InventoryGroup.CONTRACT)
      return (
        <>
          <Input
            type="date"
            onChangeDate={(date) => setInventoryEntry({ ...inventoryEntry, date })}
            value={inventoryEntry.date}
            label={t('pages.inventory.createEdit.inputLabels.general.date')}
          />
          <Checkbox
            isChecked={inventoryEntry.reminderMail}
            onCheck={(reminderMail) => updateInfo(reminderMail, 'reminderMail')}
            label={t('pages.inventory.createEdit.inputLabels.general.reminderMail')}
          />
          <Input
            type="text"
            onChange={(partner) => updateInfo(partner, 'contractPartner')}
            value={inventoryEntry.contractPartner}
            label={t('pages.inventory.createEdit.inputLabels.general.contractPartner')}
          />
        </>
      );
    else
      return (
        <>
          <Input
            disabled
            maxValue={99999}
            value={inventoryEntry.number.toString().padStart(5, '0')}
            onChangeNumber={(newValue) => updateInfo(newValue, 'number')}
            type="number"
            label={t('pages.inventory.createEdit.inputLabels.general.number')}
          />
          <TextArea
            value={inventoryEntry.note || ''}
            onChange={(newValue) => updateInfo(newValue, 'note')}
            label={t('pages.inventory.createEdit.inputLabels.general.note')}
          />
          <Input
            type="date"
            onChangeDate={(date) => setInventoryEntry({ ...inventoryEntry, date })}
            value={inventoryEntry.date}
            label={t('pages.inventory.createEdit.inputLabels.general.date')}
          />
          <Input
            value={inventoryEntry.producent}
            onChange={(newValue) => updateInfo(newValue, 'producent')}
            type="text"
            label={t('pages.inventory.createEdit.inputLabels.general.producent')}
          />
          <Input
            value={inventoryEntry.serialNumber}
            onChange={(newValue) => updateInfo(newValue, 'serialNumber')}
            type="text"
            label={t('pages.inventory.createEdit.inputLabels.general.serialNumber')}
          />
          <Checkbox
            isChecked={inventoryEntry.reminderMail}
            onCheck={(reminderMail) => updateInfo(reminderMail, 'reminderMail')}
            label={t('pages.inventory.createEdit.inputLabels.general.reminderMail')}
          />
          <Input
            value={inventoryEntry.stationClass}
            onChange={(newValue) => updateInfo(newValue, 'stationClass')}
            type="text"
            label={t('pages.inventory.createEdit.inputLabels.general.stationClass')}
          />
          <Input
            value={inventoryEntry.stationNumber}
            onChange={(newValue) => updateInfo(newValue, 'stationNumber')}
            type="text"
            label={t('pages.inventory.createEdit.inputLabels.general.stationNumber')}
          />
          <Input
            value={inventoryEntry.price}
            onChangeNumber={(newValue) => updateInfo(newValue, 'price')}
            type="number"
            label={t('pages.inventory.createEdit.inputLabels.general.price')}
          />
          {inventoryEntry.group === InventoryGroup.TOOL && (
            <>
              <Dropdown
                onChange={(toolingGroup) =>
                  setInventoryEntry({
                    ...inventoryEntry,
                    toolingGroup: toolingGroup as ToolingGroup,
                  })
                }
                label={t('pages.inventory.createEdit.inputLabels.general.toolGroup')}
                options={generateDropdownOptionsForToolingGroups()}
                selectedOption={inventoryEntry.toolingGroup}
              />
              <Dropdown
                selectedOption={inventoryEntry.toolType}
                onChange={(toolType) =>
                  setInventoryEntry({
                    ...inventoryEntry,
                    toolType: toolType as ToolingType,
                  })
                }
                label={t('pages.inventory.createEdit.inputLabels.general.toolType')}
                options={generateDropdownOptionsForToolingTypes()}
              />
            </>
          )}
        </>
      );
  }, [inventoryEntry, t]);
  return (
    <form
      className="inventory-edit"
      ref={form}
      onSubmit={(evt) => evt.preventDefault()}
      onKeyDown={preventSubmitOnEnter}
    >
      <TopBar
        title={t(`pages.inventory.title${inventoryEntry.group}`)}
        onBackClick={() =>
          navigate(`/inventory/${inventoryEntry.group.toLocaleLowerCase()}`, {
            state: { selectedCategories: location.state?.selectedCategories },
          })
        }
      >
        {inventoryEntry.id && (
          <Button
            value={t('general.buttons.delete')}
            backgroundColor="#BC2E46"
            type="button"
            onClick={() => toggleDeletePopup(true)}
          />
        )}
        <SaveButtons handleSubmit={handleSubmit} buttonRef={button} />
      </TopBar>
      <Popup
        isOpen={showDeletePopup}
        title={t('pages.inventory.createEdit.deletePopup.title')}
        buttons={[
          <Button
            value={t('general.buttons.cancel')}
            type="button"
            onClick={() => toggleDeletePopup(false)}
          />,
          <Button
            value={t('general.buttons.delete')}
            backgroundColor="#BC2E46"
            type="button"
            onClick={() =>
              deleteInventoryEntry(axios, inventoryEntry.id).then(
                (success) =>
                  success &&
                  navigate(`/inventory/${inventoryEntry.group.toLocaleLowerCase()}`, {
                    state: {
                      selectedCategories: location.state?.selectedCategories,
                    },
                  }),
              )
            }
          />,
        ]}
      >
        <p>
          {t('pages.inventory.createEdit.deletePopup.content', {
            replace: {
              NAME: inventoryEntry.name,
              NUMBER: inventoryEntry.number.toString().padStart(5, '0'),
            },
          })}
        </p>
      </Popup>
      {checkToEdit && (
        <Popup
          isOpen={!!checkToEdit}
          onClose={() => setCheckToEdit(undefined)}
          title={t('pages.inventory.createEdit.checkEdit')}
          buttons={[
            <Button
              value={t('general.buttons.cancel')}
              onClick={() => setCheckToEdit(undefined)}
            />,
            <Button
              value={t('general.buttons.add')}
              onClick={() => {
                const isUpdate: boolean = inventoryEntry.checks.some(
                  (check) => check.id === checkToEdit.id,
                );
                if (isUpdate)
                  setInventoryEntry({
                    ...inventoryEntry,
                    checks: inventoryEntry.checks.map((check) =>
                      check.id === checkToEdit.id ? checkToEdit : check,
                    ),
                  });
                else
                  setInventoryEntry({
                    ...inventoryEntry,
                    checks: [...inventoryEntry.checks, checkToEdit],
                  });
                setCheckToEdit(undefined);
              }}
            />,
          ]}
        >
          <>
            <Input type="date" value={checkToEdit?.createDate} disabled />
            <Input
              type="text"
              label={t('pages.inventory.createEdit.comment')}
              onChange={(comment) => setCheckToEdit({ ...checkToEdit, comment })}
              value={checkToEdit.comment}
            />
            <Checkbox
              isChecked={checkToEdit.passed}
              onCheck={(passed) => setCheckToEdit({ ...checkToEdit, passed })}
              label={t('pages.inventory.createEdit.success')}
            />
          </>
        </Popup>
      )}
      {checkToDelete && (
        <Popup
          isOpen={!!checkToDelete}
          title={t('pages.inventory.createEdit.checkDelete')}
          onClose={() => setCheckToDelete(undefined)}
          buttons={[
            <Button
              value={t('general.buttons.cancel')}
              onClick={() => setCheckToDelete(undefined)}
            />,
            <Button
              value={t('general.buttons.confirm')}
              onClick={() => {
                setInventoryEntry({
                  ...inventoryEntry,
                  checks: inventoryEntry.checks.filter((check) => check.id !== checkToDelete),
                });
                setCheckToDelete(undefined);
              }}
            />,
          ]}
        >
          <p>{t('pages.inventory.createEdit.checkDeleteConfirmation')}</p>
        </Popup>
      )}
      <Box>
        <fieldset>
          <legend>{t('pages.inventory.createEdit.basicInfo')}</legend>
          <Checkbox
            isChecked={!inventoryEntry.disabled}
            onCheck={(checked) => updateInfo(!checked, 'disabled')}
            label={t('pages.inventory.createEdit.inputLabels.general.active')}
          />
          <div className="three-columns">
            <Dropdown
              options={allCategories.map((category) => ({
                label: category.translations.get(user.language || Language.DE) || category.title,
                value: category.id,
              }))}
              selectedOption={inventoryEntry.categoryId}
              label={t(
                `pages.inventory.createEdit.inputLabels.general.${
                  inventoryEntry.group === InventoryGroup.CONTRACT ? 'type' : 'inventoryType'
                }`,
              )}
              onChange={(selectedGroup) => {
                setInventoryEntry({
                  ...inventoryEntry,
                  categoryId: selectedGroup,
                  metaInformations: new Map<string, string>(),
                });
              }}
            />
            <Dropdown
              label={t('pages.inventory.createEdit.inputLabels.general.officeId')}
              onChange={(officeId) => updateInfo(officeId, 'officeId')}
              selectedOption={inventoryEntry.officeId}
              options={allOffices.map((office) => ({
                label: office.name,
                value: office.id,
              }))}
            />
            <Dropdown
              label={t('pages.inventory.createEdit.inputLabels.general.simpleUsers')}
              clearable
              onChange={(currentUser) => updateInfo(currentUser, 'currentUserId')}
              selectedOption={inventoryEntry.currentUserId}
              options={generateDropdownOptionsForSimpleUser(
                allSimpleUser.filter(
                  (user) => !user.disabled || inventoryEntry.currentUserId === user.id,
                ),
              )}
            />
            <Input
              value={inventoryEntry.name}
              onChange={(newValue) => updateInfo(newValue, 'name')}
              type="text"
              label={t('pages.inventory.createEdit.inputLabels.general.name')}
            />
            {variableFields}
          </div>
        </fieldset>
      </Box>
      <Box
        title={
          <div className="user-create__box-title-wrapper">
            <p className="box__title">{t('pages.inventory.createEdit.detailInformation')}</p>
            {stateIcon && (
              <IconComponent
                type={stateIcon}
                onClick={handleInventoryStateUpdate}
                className="user-create__box-title-wrapper__icon clickable"
              />
            )}
          </div>
        }
      >
        {category && (
          <InventoryDetailInformation
            category={category!}
            entry={inventoryEntry}
            update={setInventoryEntry}
          />
        )}
      </Box>
      <Box title={t('pages.inventory.createEdit.checks')}>
        <div className="inventory-edit__check-wrapper">
          <IconComponent
            type={IconType.SEARCH}
            className="inventory-edit__check-wrapper__check-icon"
            width={30}
            onClick={() => setCheckToEdit(generateEmptyInventoryCheck(user.id))}
          />
          <Table
            hideTopFilter
            header={
              t('pages.inventory.createEdit.tableHeaderChecks', {
                returnObjects: true,
              }) as TableHeader[]
            }
            rows={inventoryCheckRows}
          />
        </div>
      </Box>
      <Box>
        <fieldset>
          <legend>{t('pages.inventory.createEdit.userHistory')}</legend>
          <Table
            header={
              t('pages.inventory.createEdit.userHistoryTitles', {
                returnObjects: true,
              }) as TableHeader[]
            }
            rows={userHistoryTableEntries}
          />
        </fieldset>
      </Box>
      {inventoryEntry.id && (
        <FileOverview
          path={`inventory/${inventoryEntry.group}/${inventoryEntry.id}/`}
          bucket={DocumentBucket.INVENTORY}
        />
      )}
    </form>
  );
};

export default InventoryCreateOrEdit;
